import { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, BookCheck } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const EMPTY_LINE = { ledger: "", amount: 0, is_debit: true };

export default function AuditJVs({ companyContext }) {
  const { companyId, branchId } = companyContext || {};
  const { hasRole } = useAuth();
  const canPost = hasRole("admin", "reviewer");

  // Stable client-side row IDs for the JV-line form. We can't use array
  // index as the React key because lines get inserted/removed mid-list
  // and indices shift — that loses input focus and confuses controlled
  // <Input> components.
  const newLineId = () =>
    `ln-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    number: "",
    narration: "",
    party: "",
    lines: [
      { uid: newLineId(), ledger: "", amount: 0, is_debit: true },
      { uid: newLineId(), ledger: "", amount: 0, is_debit: false },
    ],
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-jvs`, {
        params: { company_id: companyId, ...(branchId ? { branch_id: branchId } : {}) },
      });
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (companyId) load(); /* eslint-disable-next-line */ }, [companyId, branchId]);

  const totals = form.lines.reduce(
    (acc, ln) => {
      const amt = Number(ln.amount) || 0;
      if (ln.is_debit) acc.dr += amt;
      else acc.cr += amt;
      return acc;
    },
    { dr: 0, cr: 0 },
  );
  const balanced = Math.abs(totals.dr - totals.cr) < 0.01 && totals.dr > 0;

  const updateLine = (i, patch) => {
    const ls = form.lines.map((ln, idx) => (idx === i ? { ...ln, ...patch } : ln));
    setForm({ ...form, lines: ls });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { uid: newLineId(), ...EMPTY_LINE }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });

  const submit = async () => {
    try {
      await axios.post(`${API}/audit-jvs`, {
        company_id: companyId,
        branch_id: branchId,
        date: form.date,
        number: form.number,
        narration: form.narration,
        party: form.party,
        lines: form.lines.map((ln) => ({ ledger: ln.ledger.trim(), amount: Number(ln.amount) || 0, is_debit: !!ln.is_debit })),
      });
      toast.success("Audit JV posted");
      setOpen(false);
      setForm({
        date: new Date().toISOString().slice(0, 10),
        number: "", narration: "", party: "",
        lines: [
          { uid: newLineId(), ledger: "", amount: 0, is_debit: true },
          { uid: newLineId(), ledger: "", amount: 0, is_debit: false },
        ],
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (v) => {
    if (!window.confirm(`Delete audit JV ${v.number}?`)) return;
    try {
      await axios.delete(`${API}/audit-jvs/${v.voucher_id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-jvs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-[Chivo] font-semibold tracking-tight">Audit JVs</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Post adjustments made during review without touching the source daybook. Audit JVs bypass month locks
            and are tagged so you can reconcile pre-audit vs post-audit numbers.
          </p>
        </div>
        {canPost && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-audit-jv-button" className="bg-zinc-950 text-white hover:bg-zinc-800">
                <Plus className="h-4 w-4 mr-2" /> New Audit JV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl" data-testid="audit-jv-dialog">
              <DialogHeader>
                <DialogTitle>Post Audit JV</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-4 gap-3 py-2">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ajv-date" />
                </div>
                <div>
                  <Label>Voucher #</Label>
                  <Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="auto" data-testid="ajv-number" />
                </div>
                <div className="col-span-2">
                  <Label>Party (optional)</Label>
                  <Input value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} data-testid="ajv-party" />
                </div>
                <div className="col-span-4">
                  <Label>Narration</Label>
                  <Textarea rows={2} value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} data-testid="ajv-narration" />
                </div>
              </div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="p-2 text-left w-[45%]">Ledger</th>
                      <th className="p-2 text-right">Debit</th>
                      <th className="p-2 text-right">Credit</th>
                      <th className="p-2 w-[40px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lines.map((ln, i) => (
                      <tr key={ln.uid} className="border-t">
                        <td className="p-1.5">
                          <Input value={ln.ledger} onChange={(e) => updateLine(i, { ledger: e.target.value })} data-testid={`ajv-line-${i}-ledger`} />
                        </td>
                        <td className="p-1.5">
                          <Input
                            type="number" step="0.01"
                            className="text-right tabular-nums"
                            value={ln.is_debit ? ln.amount : ""}
                            onChange={(e) => updateLine(i, { amount: e.target.value, is_debit: true })}
                            data-testid={`ajv-line-${i}-dr`}
                          />
                        </td>
                        <td className="p-1.5">
                          <Input
                            type="number" step="0.01"
                            className="text-right tabular-nums"
                            value={!ln.is_debit ? ln.amount : ""}
                            onChange={(e) => updateLine(i, { amount: e.target.value, is_debit: false })}
                            data-testid={`ajv-line-${i}-cr`}
                          />
                        </td>
                        <td className="p-1.5 text-center">
                          {form.lines.length > 2 && (
                            <button onClick={() => removeLine(i)} className="text-rose-600 hover:text-rose-700" data-testid={`ajv-line-${i}-remove`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t bg-zinc-50 font-[IBM_Plex_Mono] tabular-nums">
                      <td className="p-2 text-zinc-500 text-xs">Totals</td>
                      <td className="p-2 text-right">{fmt(totals.dr)}</td>
                      <td className="p-2 text-right">{fmt(totals.cr)}</td>
                      <td className="p-2 text-center">
                        {balanced
                          ? <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-300">OK</Badge>
                          : <Badge className="bg-rose-500/15 text-rose-700 border-rose-300">Off</Badge>}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div>
                <Button variant="outline" size="sm" onClick={addLine} data-testid="ajv-add-line">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add line
                </Button>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={!balanced} data-testid="ajv-submit">Post</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookCheck className="h-4 w-4" /> Posted Audit JVs ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Vch #</TableHead>
                <TableHead>Narration</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Posted by</TableHead>
                {canPost && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={canPost ? 6 : 5} className="text-center text-zinc-500 py-6">Loading…</TableCell></TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow><TableCell colSpan={canPost ? 6 : 5} className="text-center text-zinc-500 py-6">No audit JVs yet.</TableCell></TableRow>
              )}
              {!loading && items.map((v) => (
                <Fragment key={v.voucher_id}>
                  <TableRow data-testid={`ajv-row-${v.number}`}>
                    <TableCell className="font-[IBM_Plex_Mono] tabular-nums">{v.date}</TableCell>
                    <TableCell className="font-medium">{v.number}</TableCell>
                    <TableCell className="text-zinc-700">
                      {v.narration || <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="text-right font-[IBM_Plex_Mono] tabular-nums">{fmt(v.amount)}</TableCell>
                    <TableCell className="text-xs text-zinc-500">{v.created_by_email || "—"}</TableCell>
                    {canPost && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="text-rose-600" onClick={() => del(v)} data-testid={`ajv-delete-${v.number}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow className="bg-zinc-50/50">
                    <TableCell colSpan={canPost ? 6 : 5} className="py-2">
                      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-600">
                        {v.lines?.map((ln, i) => (
                          <span key={i} className="font-[IBM_Plex_Mono]">
                            {ln.is_debit ? "Dr " : "Cr "}
                            <span className="text-zinc-800">{ln.ledger}</span>
                            <span className="ml-2 tabular-nums">{fmt(ln.amount)}</span>
                          </span>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
